import { html, css, LitElement } from 'lit-element';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

import '@material/mwc-button';
import '@material/mwc-textfield';

import { db } from './intrivity_app.js';
import { hasRole } from './utility.js';
import { a11yStyles, disclosureButton, uniqueId } from './a11y.js';
import { auditLabel } from './audit_log.js';
import { t, Localized, formatDate } from './i18n.js';

// Viewer for the administrative audit trail (F-06). SuperAdmin only, matching
// the Firestore rule that governs reads.
//
// A log nobody can read is not an audit trail, so this exists to answer the
// question an institution actually asks: who did what to whom, and when.
export class IntrivityAdminAuditLog extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .section-header {
                margin: 0 0 5px 0;
                width: 100%;
                box-sizing: border-box;
                color: black;
                font-size: 1.5em;
                font-weight: normal;
                padding: 15px 10px 10px 30px;
                background-color: gray;
            }
            .section-header .disclosure {
                vertical-align: middle;
                margin-left: 8px;
                padding: 4px;
            }
            .body { padding: 10px 30px 30px; }
            .intro { max-width: 70ch; color: #444; line-height: 1.55; }
            .controls {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
                align-items: center;
                margin: 16px 0 8px;
            }
            mwc-textfield { width: 260px; }
            .table-scroll { overflow-x: auto; max-width: 100%; }
            table {
                border-collapse: collapse;
                width: 100%;
                min-width: 720px;
                font-size: 0.9em;
            }
            caption { text-align: left; padding: 6px 0; color: #5c5c5c; }
            th, td {
                border-bottom: 1px solid #e4e4e4;
                padding: 8px 10px;
                text-align: left;
                vertical-align: top;
            }
            thead th {
                border-bottom: 1px solid #bbb;
                font-size: 0.82em;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: #5c5c5c;
                font-weight: 600;
            }
            td.when { white-space: nowrap; color: #6b6b6b; }
            td.detail {
                font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
                font-size: 0.85em;
                color: #444;
                max-width: 30ch;
                overflow-wrap: anywhere;
            }
            .empty { color: #6b6b6b; padding: 14px 0; }
            .weak-auth {
                font-weight: 400;
                font-size: 0.85em;
                color: #8a3324;
                margin-top: 2px;
            }
            .ok-auth {
                font-weight: 400;
                font-size: 0.85em;
                color: #6b6b6b;
                margin-top: 2px;
            }
            .note {
                margin-top: 18px;
                max-width: 70ch;
                border-left: 4px solid rgb(166, 97, 0);
                background: #fdf8ee;
                padding: 12px 14px;
                font-size: 0.9em;
                color: #444;
            }
        `];
    }

    static get properties() {
        return {
            user_profile: { type: Object },
            expanded: { type: Boolean },
            _entries: { type: Array },
            _loading: { type: Boolean },
            _error: { type: String },
            _filter: { type: String },
        };
    }

    constructor() {
        super();
        this._panelId = uniqueId('audit-log');
        this.expanded = false;
        this._entries = [];
        this._loading = false;
        this._error = '';
        this._filter = '';
    }

    updated(changed) {
        if (changed.has('expanded') && this.expanded && !this._loading && !this._entries.length) {
            this._load();
        }
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            const snap = await getDocs(query(
                collection(db, 'audit_log'), orderBy('at', 'desc'), limit(250)));
            this._entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
            this._error = t('auditLog.loadError', { message: e.message || e });
        } finally {
            this._loading = false;
        }
    }

    _when(entry) {
        // `at` is a server timestamp and is briefly null between the local
        // write and the server acknowledging it; clientAt covers that window.
        const d = entry.at && entry.at.toDate ? entry.at.toDate()
                : entry.clientAt ? new Date(entry.clientAt) : null;
        if (!d || isNaN(d)) return '—';
        return formatDate(d, { dateStyle: 'medium', timeStyle: 'medium' });
    }

    // F-05. Flags the entries worth acting on: a privileged action taken from
    // an account that has only a password. Shown inline rather than left in the
    // detail JSON, because the point of recording it is to answer one question
    // at a glance -- can PRIVILEGED_AUTH_POLICY be raised without locking
    // anyone out?
    _signIn(entry) {
        const s = entry.signIn || (entry.detail && entry.detail.auth) || null;
        if (!s) return html``;

        // Client entries carry {providers, password_only}; server entries
        // carry {provider, level} from the verified token.
        const weak = s.password_only === true || s.level === 'password';
        const label = s.providers ? s.providers.join(', ') : (s.provider || '');
        if (!label) return html``;

        return weak
            ? html`<div class="weak-auth" title=${t('auditLog.weakTitle')}>
                       ⚠ ${t('auditLog.weak', { label })}
                   </div>`
            : html`<div class="ok-auth">${label}</div>`;
    }

    _matches(entry) {
        const f = this._filter.trim().toLowerCase();
        if (!f) return true;
        return [entry.actor, entry.subject, entry.event, JSON.stringify(entry.detail || {})]
            .some((v) => (v || '').toString().toLowerCase().includes(f));
    }

    _renderTable(rows) {
        if (this._loading) return html`<p class="empty" role="status">${t('auditLog.loading')}</p>`;
        if (!this._entries.length) return html`<p class="empty">${t('auditLog.none')}</p>`;
        if (!rows.length) return html`<p class="empty">${t('auditLog.noMatch', { filter: this._filter })}</p>`;
        return html`
            <div class="table-scroll">
                <table>
                    <caption>${t('auditLog.caption', { shown: rows.length, total: this._entries.length })}</caption>
                    <thead>
                        <tr>
                            <th scope="col">${t('auditLog.colWhen')}</th>
                            <th scope="col">${t('auditLog.colWho')}</th>
                            <th scope="col">${t('auditLog.colWhat')}</th>
                            <th scope="col">${t('auditLog.colWhom')}</th>
                            <th scope="col">${t('auditLog.colDetail')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map((e) => html`
                            <tr>
                                <td class="when">${this._when(e)}</td>
                                <th scope="row" style="font-weight: 600;">
                                    ${e.actor || html`<span style="font-weight:400;color:#6b6b6b;">${t('auditLog.notRecorded')}</span>`}
                                    ${e.source === 'server-trigger'
                                        ? html`<div style="font-weight:400;font-size:0.85em;color:#6b6b6b;">${t('auditLog.serverTrigger')}</div>`
                                        : html``}
                                    ${this._signIn(e)}
                                </th>
                                <td>${auditLabel(e.event)}</td>
                                <td>${e.subject || '—'}</td>
                                <td class="detail">${e.detail ? JSON.stringify(e.detail) : '—'}</td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `;
    }

    render() {
        if (!hasRole('superadmin', this.user_profile)) return html``;
        const rows = this._entries.filter((e) => this._matches(e));

        return html`
            <h2 class="section-header">
                ${t('auditLog.title')}
                ${disclosureButton(this.expanded, t('auditLog.title'), this._panelId, () => { this.expanded = !this.expanded; })}
            </h2>

            ${this.expanded ? html`
                <div id=${this._panelId} class="body">
                    <p class="intro">
                        ${t('auditLog.intro')}
                    </p>

                    <div class="controls">
                        <mwc-textfield
                            label=${t('auditLog.filter')}
                            placeholder=${t('auditLog.filterPlaceholder')}
                            .value=${this._filter}
                            @input=${(e) => this._filter = e.target.value}></mwc-textfield>
                        <mwc-button outlined
                            label=${this._loading ? t('common.loading') : t('auditLog.refresh')}
                            ?disabled=${this._loading}
                            @click=${() => { this._entries = []; this._load(); }}></mwc-button>
                    </div>

                    <div role="status" aria-live="polite" style="min-height:1.3em; color:#a3341f;">
                        ${this._error}
                    </div>

                    ${this._renderTable(rows)}

                    <p class="note">
                        ${t('auditLog.note')}
                    </p>
                </div>
            `: html``}
        `;
    }
}

if (!customElements.get('intrivity-admin-audit-log')) {
    customElements.define('intrivity-admin-audit-log', IntrivityAdminAuditLog);
}
